import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { isAuthenticated } from '@/lib/auth-store'
import heroCommandCenterGraphic from '@/assets/landing/hero-command-center.svg'
import voiceProfileLabGraphic from '@/assets/landing/voice-profile-lab.svg'
import weeklyCadenceBoardGraphic from '@/assets/landing/weekly-cadence-board.svg'
import engagementRadarGraphic from '@/assets/landing/engagement-radar.svg'
import warmLeadQueueGraphic from '@/assets/landing/warm-lead-queue.svg'
import advisorActionFeedGraphic from '@/assets/landing/advisor-action-feed.svg'
import extensionSyncStatusGraphic from '@/assets/landing/extension-sync-status.svg'
import outreachPlaybookGraphic from '@/assets/landing/outreach-playbook.svg'
import styles from './index.module.css'

const MONTHLY_PRICE = 29
const YEARLY_PRICE = 299
const YEARLY_EFFECTIVE_MONTHLY = Number((YEARLY_PRICE / 12).toFixed(2))
const YEARLY_SAVINGS = MONTHLY_PRICE * 12 - YEARLY_PRICE
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

export const Route = createFileRoute('/')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/home' })
    }
  },
  component: LandingRoute,
})

function LandingRoute() {
  return (
    <section className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.heroCopy}>
          <p className={styles.eyebrow}>For professionals building authority on LinkedIn</p>
          <h1 className={styles.title}>
            Create in your voice. Stay on cadence. Know who to message next.
          </h1>
          <p className={styles.subtitle}>
            StatureBoost is the 20-minute LinkedIn growth system for busy operators: generate strong
            drafts, queue your week, and act on clear engagement-driven outreach priorities.
          </p>

          <div className={styles.heroActions}>
            <Link to="/signup" className={styles.primaryCta}>
              Start 14-day trial
            </Link>
            <Link to="/workspace" className={styles.secondaryCta}>
              See walkthrough
            </Link>
          </div>

          <p className={styles.meta}>
            Card required • Browser extension sync • Credentials stay in your browser
          </p>

          <div className={styles.proofRow}>
            <p>Voice-matched drafts</p>
            <p>Weekly scheduling loop</p>
            <p>Prescriptive advisor cards</p>
            <p>Warm-lead queue</p>
          </div>
        </div>

        <div className={styles.heroVisual}>
          <img
            src={heroCommandCenterGraphic}
            alt="StatureBoost command center mock showing drafts, schedule, and warm lead queue"
            className={styles.heroImage}
            loading="eager"
          />
        </div>
      </header>

      <section className={styles.kpiStrip} aria-label="Workflow outcomes">
        <article className={styles.kpiCard}>
          <p className={styles.kpiLabel}>Creation</p>
          <p className={styles.kpiValue}>3 voice-safe variants</p>
          <p className={styles.kpiDetail}>Topic, link, notes, or transcript inputs.</p>
        </article>
        <article className={styles.kpiCard}>
          <p className={styles.kpiLabel}>Cadence</p>
          <p className={styles.kpiValue}>Weekly queue visibility</p>
          <p className={styles.kpiDetail}>Know your gaps before reach drops.</p>
        </article>
        <article className={styles.kpiCard}>
          <p className={styles.kpiLabel}>Insights</p>
          <p className={styles.kpiValue}>Actionable post analytics</p>
          <p className={styles.kpiDetail}>Focus on what drove comments and saves.</p>
        </article>
        <article className={styles.kpiCard}>
          <p className={styles.kpiLabel}>Outreach</p>
          <p className={styles.kpiValue}>Prioritized warm leads</p>
          <p className={styles.kpiDetail}>Know who to follow up with first.</p>
        </article>
      </section>

      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <p className={styles.sectionEyebrow}>Execution System</p>
          <h2 className={styles.sectionTitle}>
            Built for focused weekly execution, not dashboard overload.
          </h2>
        </header>

        <div className={styles.storyGrid}>
          <article className={styles.storyRow}>
            <div className={styles.storyMedia}>
              <img
                src={voiceProfileLabGraphic}
                alt="Voice profile workspace mock with role, industry, audience, and tone controls"
                className={styles.storyImage}
                loading="lazy"
              />
            </div>
            <div className={styles.storyContent}>
              <h3>Write like yourself, not like a template</h3>
              <p>
                Capture your role, audience, and tone once. Every draft uses that context so output
                stays aligned with your real voice.
              </p>
              <ul className={styles.points}>
                <li>Structured voice profile, not generic prompt hacks</li>
                <li>Built for practical professional writing</li>
                <li>Fast iteration without losing tone consistency</li>
              </ul>
            </div>
          </article>

          <article className={`${styles.storyRow} ${styles.storyRowReverse}`}>
            <div className={styles.storyMedia}>
              <img
                src={weeklyCadenceBoardGraphic}
                alt="Weekly posting cadence board with scheduled slots"
                className={styles.storyImage}
                loading="lazy"
              />
            </div>
            <div className={styles.storyContent}>
              <h3>Stay visible without daily effort</h3>
              <p>
                Batch-create and schedule in one session. If your queue is thin, you get clear
                prompts before inconsistency becomes a pipeline problem.
              </p>
              <ul className={styles.points}>
                <li>Week-first scheduling model</li>
                <li>Gap detection for low-cadence weeks</li>
                <li>Built for busy consulting and operator workflows</li>
              </ul>
            </div>
          </article>

          <article className={styles.storyRow}>
            <div className={styles.storyMedia}>
              <img
                src={engagementRadarGraphic}
                alt="Engagement analytics panel showing trends and top-performing content"
                className={styles.storyImage}
                loading="lazy"
              />
            </div>
            <div className={styles.storyContent}>
              <h3>Know what worked and who to contact next</h3>
              <p>
                StatureBoost turns post engagement into a prioritized warm-lead queue with concrete
                follow-up actions.
              </p>
              <ul className={styles.points}>
                <li>See which posts drive comments, saves, and conversations</li>
                <li>Rank repeat engagers by recency and intent</li>
                <li>Act from one list instead of scanning every post manually</li>
              </ul>
            </div>
          </article>
        </div>
      </section>

      <section className={styles.visualSection}>
        <header className={styles.sectionHeader}>
          <p className={styles.sectionEyebrow}>Action Layer</p>
          <h2 className={styles.sectionTitle}>Clear recommendations. Fast follow-through.</h2>
        </header>

        <div className={styles.visualGrid}>
          <article className={styles.visualCard}>
            <img
              src={advisorActionFeedGraphic}
              alt="AI advisor card feed with recommendation and action buttons"
              className={styles.visualImage}
              loading="lazy"
            />
            <h3>Prescriptive advisor cards</h3>
            <p>Get direct next steps instead of interpreting complex chart dashboards.</p>
          </article>

          <article className={styles.visualCard}>
            <img
              src={warmLeadQueueGraphic}
              alt="Warm lead queue with ranked engaged people and suggested follow-up actions"
              className={styles.visualImage}
              loading="lazy"
            />
            <h3>Warm lead prioritization</h3>
            <p>Focus on high-signal people already engaging with your content.</p>
          </article>

          <article className={styles.visualCard}>
            <img
              src={outreachPlaybookGraphic}
              alt="Outreach playbook showing lead context and message recommendations"
              className={styles.visualImage}
              loading="lazy"
            />
            <h3>Outreach guidance</h3>
            <p>Move from engagement insight to personalized outreach in one flow.</p>
          </article>
        </div>
      </section>

      <section className={`${styles.section} ${styles.trustSection}`}>
        <div className={styles.trustMedia}>
          <img
            src={extensionSyncStatusGraphic}
            alt="Extension sync panel showing installed, connected, and recent sync status"
            className={styles.storyImage}
            loading="lazy"
          />
        </div>
        <div className={styles.trustContent}>
          <p className={styles.sectionEyebrow}>Trust + Control</p>
          <h2 className={styles.sectionTitle}>
            Extension-powered data sync with transparent status.
          </h2>
          <p>
            Keep visibility into installation, connection health, and latest sync status so you
            always know where your data pipeline stands.
          </p>
          <p className={styles.trustNote}>
            Privacy-first architecture: credentials stay in your browser session.
          </p>
        </div>
      </section>

      <section className={`${styles.section} ${styles.pricingSection}`}>
        <header className={styles.sectionHeader}>
          <p className={styles.sectionEyebrow}>Pricing</p>
          <h2 className={styles.sectionTitle}>Simple pricing for serious consistency.</h2>
        </header>
        <p className={styles.sectionIntro}>
          One plan designed for solo professionals and lean teams building pipeline from personal
          brand.
        </p>

        <div className={styles.pricingGrid}>
          <article className={styles.priceCard}>
            <p className={styles.planName}>Monthly</p>
            <p className={styles.price}>${MONTHLY_PRICE}/month</p>
            <p className={styles.planMeta}>Flexible month-to-month access.</p>
          </article>

          <article className={`${styles.priceCard} ${styles.recommended}`}>
            <p className={styles.badge}>Best value</p>
            <p className={styles.planName}>Yearly</p>
            <p className={styles.price}>${YEARLY_PRICE}/year</p>
            <p className={styles.planMeta}>
              {USD.format(YEARLY_EFFECTIVE_MONTHLY)}/month effective • save{' '}
              {USD.format(YEARLY_SAVINGS)} per year
            </p>
          </article>
        </div>
      </section>

      <section className={styles.finalCta}>
        <h2>Replace random posting with a weekly growth routine.</h2>
        <p>
          Start your trial, publish your first post, and set your next week in one focused session.
        </p>
        <div className={styles.heroActions}>
          <Link to="/signup" className={styles.primaryCta}>
            Start free trial
          </Link>
          <Link to="/workspace" className={styles.secondaryCta}>
            See walkthrough
          </Link>
        </div>
      </section>
    </section>
  )
}
